import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpen, Download, FileText, Plus, Wallet,
  Pencil, Trash2, ArrowDownLeft, ArrowUpRight, AlertCircle,
} from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert';
import cashbookService from '../../services/CashbookService.js';
import CashbookOpeningBalanceModal from './CashbookOpeningBalanceModal.js';
import CashbookEntryModal from './CashbookEntryModal.js';
import { useAuth } from '../../context/AuthContext.js';
import { locationHubService } from '../../services/locationHubService.js';
import { formatHubBranchLabel, filterShipmentLocations } from '../../utils/destinationType.js';
import { formatIndianDate, formatIndianTime, formatTimeAgo } from '../../utils/localDate.js';
import SelectField from '../common/SelectField.js';
import ListPagination from '../common/ListPagination.js';
import { paginateArray } from '../../utils/clientPaginate.js';

const getToday = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const money = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CashbookWhen = ({ txn }) => {
  const bookDate = txn?.date;
  const moment = txn?.occurred_at;
  const hasTime = moment && !/^\d{4}-\d{2}-\d{2}$/.test(String(moment).trim());
  const ago = formatTimeAgo(hasTime ? moment : bookDate);
  return (
    <div className="leading-5 text-gray-900">
      <div className="whitespace-nowrap">{formatIndianDate(bookDate || moment)}</div>
      {hasTime ? <div className="whitespace-nowrap">{formatIndianTime(moment)}</div> : null}
      {ago ? (
        <div className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">{ago}</div>
      ) : null}
    </div>
  );
};

const downloadBlob = (data, filename, type) => {
  const blob = new Blob([data], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const SummaryCard = ({ title, cash, bank, cashColor = 'text-green-600', bankColor = 'text-blue-600' }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
    <p className="text-sm font-semibold text-gray-700 mb-3">{title}</p>
    <div className="space-y-1.5 text-sm">
      <div className="flex justify-between">
        <span className="text-gray-500">Cash:</span>
        <span className={`font-bold ${cashColor}`}>{money(cash)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500">Bank:</span>
        <span className={`font-bold ${bankColor}`}>{money(bank)}</span>
      </div>
    </div>
  </div>
);

const ModeBadge = ({ mode }) => (
  <span className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] sm:text-xs font-semibold uppercase whitespace-nowrap">
    {(mode || '').replace(/_/g, ' ') || '—'}
  </span>
);

const RowMeta = ({ t }) => {
  const bits = [
    t.created_by_name,
    t.location_hub_label,
    t.approved_by_name ? `Approved: ${t.approved_by_name}` : null,
    t.reason && t.reason !== t.details ? t.reason : null,
  ].filter(Boolean);
  if (!bits.length && t.include_in_totals !== false) return null;
  return (
    <div className="text-[11px] text-gray-400 mt-0.5 space-y-0.5">
      {bits.length > 0 && <div>{bits.join(' · ')}</div>}
      {t.include_in_totals === false && (
        <div className="text-amber-600 font-medium">Audit only (no balance impact)</div>
      )}
    </div>
  );
};

const RowActions = ({ t, onEdit, onDelete }) =>
  t.editable ? (
    <div className="flex items-center gap-1.5">
      <button type="button" onClick={() => onEdit(t)} className="text-gray-400 hover:text-orange-600" title="Edit">
        <Pencil className="w-4 h-4" />
      </button>
      <button type="button" onClick={() => onDelete(t)} className="text-gray-400 hover:text-red-600" title="Delete">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  ) : (
    <span className="text-[10px] text-gray-300">auto</span>
  );

const PAGE_SIZE = 10;

const TxnTable = ({ title, txns, accent, onEdit, onDelete }) => {
  const [page, setPage] = useState(1);
  const view = paginateArray(txns, page, PAGE_SIZE);
  const pageRows = view.items;

  useEffect(() => {
    setPage(1);
  }, [txns]);

  if (!txns || txns.length === 0) return null;

  return (
    <div className="mb-5">
      <h4 className={`text-sm font-semibold mb-2 ${accent}`}>{title}</h4>

      <div className="hidden md:block bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2.5 text-left bg-gray-50">Date</th>
                <th className="px-3 py-2.5 text-left bg-gray-50">Details</th>
                <th className="px-3 py-2.5 text-left bg-gray-50">Mode</th>
                <th className="px-3 py-2.5 text-right bg-gray-50">Amount</th>
                <th className="px-3 py-2.5 text-right w-20 bg-gray-50">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pageRows.map((t, i) => (
                <tr key={`${t.source_type}-${t.source_id}-${i}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 align-top"><CashbookWhen txn={t} /></td>
                  <td className="px-3 py-2.5">
                    <div className="text-gray-800">{t.details}</div>
                    {(t.party_name || t.reference_no) && (
                      <div className="text-xs text-gray-500">
                        {t.party_name}{t.party_name && t.reference_no ? ' · ' : ''}{t.reference_no}
                      </div>
                    )}
                    <RowMeta t={t} />
                  </td>
                  <td className="px-3 py-2.5"><ModeBadge mode={t.payment_mode} /></td>
                  <td className={`px-3 py-2.5 text-right font-bold whitespace-nowrap ${accent}`}>{money(t.amount)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex justify-end">
                      <RowActions t={t} onEdit={onEdit} onDelete={onDelete} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="md:hidden space-y-2">
        {pageRows.map((t, i) => (
          <div
            key={`m-${t.source_type}-${t.source_id}-${i}`}
            className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm"
          >
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 break-words">{t.details}</p>
                {(t.party_name || t.reference_no) && (
                  <p className="text-xs text-gray-500 break-words">
                    {t.party_name}{t.party_name && t.reference_no ? ' · ' : ''}{t.reference_no}
                  </p>
                )}
                <RowMeta t={t} />
              </div>
              <span className={`text-sm font-bold shrink-0 ${accent}`}>{money(t.amount)}</span>
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <CashbookWhen txn={t} />
                <ModeBadge mode={t.payment_mode} />
              </div>
              <RowActions t={t} onEdit={onEdit} onDelete={onDelete} />
            </div>
          </div>
        ))}
      </div>
      <ListPagination
        currentPage={view.currentPage}
        totalPages={view.totalPages}
        onNext={() => setPage((page) => page + 1)}
      />
    </div>
  );
};

const CashbookReport = () => {
  const { user } = useAuth();
  const isHubAdmin = user?.role === 'Hub Admin';
  const canFilterHub = ['System Admin', 'Admin', 'Accountant'].includes(user?.role);
  const [fromDate, setFromDate] = useState(getToday());
  const [toDate, setToDate] = useState(getToday());
  const [locationHubId, setLocationHubId] = useState('');
  const [locationHubs, setLocationHubs] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');
  const [openingOpen, setOpeningOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [activeTab, setActiveTab] = useState('received');

  useEffect(() => {
    if (!canFilterHub) return;
    locationHubService.getHubsForSelect({ brief: 1 })
      .then((res) => setLocationHubs(filterShipmentLocations(res.data?.hubs || [])))
      .catch(() => setLocationHubs([]));
  }, [canFilterHub]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const hubParam = canFilterHub && locationHubId ? locationHubId : undefined;
      const res = await cashbookService.getRange(fromDate, toDate, hubParam);
      setReport(res.data?.data || null);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load cashbook');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, locationHubId, canFilterHub]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async (kind) => {
    setExporting(kind);
    try {
      const hubParam = canFilterHub && locationHubId ? locationHubId : undefined;
      if (kind === 'pdf') {
        const res = await cashbookService.exportPdf(fromDate, toDate, hubParam);
        downloadBlob(res.data, `cashbook_${fromDate}_${toDate}.pdf`, 'application/pdf');
      } else {
        const res = await cashbookService.exportExcel(fromDate, toDate, hubParam);
        downloadBlob(
          res.data,
          `cashbook_${fromDate}_${toDate}.xlsx`,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        );
      }
      Alert.success(`Cashbook ${kind.toUpperCase()} exported`);
    } catch (err) {
      Alert.error(`Failed to export ${kind.toUpperCase()}`);
    } finally {
      setExporting('');
    }
  };

  const handleDeleteEntry = async (t) => {
    const ok = await Alert.confirm('Delete this cashbook entry?', 'Delete Entry');
    if (!ok) return;
    try {
      await cashbookService.deleteEntry(t.id);
      Alert.success('Entry deleted');
      fetchReport();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete entry');
    }
  };

  const openAddEntry = () => { setEditingEntry(null); setEntryOpen(true); };
  const openEditEntry = (t) => { setEditingEntry(t); setEntryOpen(true); };

  const transactions = report?.transactions;
  const receivedCash = useMemo(
    () => (transactions || []).filter((t) => t.type === 'received' && t.account === 'cash'),
    [transactions],
  );
  const receivedBank = useMemo(
    () => (transactions || []).filter((t) => t.type === 'received' && t.account === 'bank'),
    [transactions],
  );
  const paidCash = useMemo(
    () => (transactions || []).filter((t) => t.type === 'paid' && t.account === 'cash'),
    [transactions],
  );
  const paidBank = useMemo(
    () => (transactions || []).filter((t) => t.type === 'paid' && t.account === 'bank'),
    [transactions],
  );

  const opening = report?.opening || { cash: 0, bank: 0 };
  const received = report?.received || { cash: 0, bank: 0 };
  const paid = report?.paid || { cash: 0, bank: 0 };
  const closing = report?.closing || { cash: 0, bank: 0 };

  const receivedTotal = (received.cash || 0) + (received.bank || 0);
  const paidTotal = (paid.cash || 0) + (paid.bank || 0);
  const receivedCount = receivedCash.length + receivedBank.length;
  const paidCount = paidCash.length + paidBank.length;

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-700 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-buttonhoverTxt" />
              Cashbook
            </h1>
            <p className="text-sm text-gray-600 mt-1">Daily cash & bank position with received / paid flow</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" icon={Wallet} onClick={() => setOpeningOpen(true)}>Opening Balance</Button>
            <Button variant="primary" icon={Plus} onClick={openAddEntry}>Add Entry</Button>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm mb-4">
          <div className={`grid grid-cols-2 ${canFilterHub ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-3`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date" value={toDate} min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 text-sm"
              />
            </div>
            {canFilterHub && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hub / Branch</label>
                <SelectField
                  variant="form"
                  value={locationHubId}
                  onChange={(e) => setLocationHubId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 text-sm"
                >
                  <option value="">All hubs</option>
                  {locationHubs.map((hub) => (
                    <option key={hub.id} value={hub.id}>
                      {formatHubBranchLabel(hub) || hub.location}
                    </option>
                  ))}
                </SelectField>
              </div>
            )}
            {isHubAdmin && (
              <div className="col-span-2 lg:col-span-1 flex items-end">
                <p className="text-xs text-gray-500 pb-2">Showing your hub/branch cashbook only</p>
              </div>
            )}
            <div className="flex items-end">
              <Button variant="secondary" icon={FileText} fullWidth
                onClick={() => handleExport('pdf')} loading={exporting === 'pdf'} disabled={!!exporting}>
                PDF
              </Button>
            </div>
            <div className="flex items-end">
              <Button variant="secondary" icon={Download} fullWidth
                onClick={() => handleExport('excel')} loading={exporting === 'excel'} disabled={!!exporting}>
                Excel
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <Loader fullScreen={false} message="Loading cashbook..." />
        ) : (
          <>
            {report && !report.has_opening_seed && (
              <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>No opening balance set yet. Balances start from zero — click <strong>Opening Balance</strong> to seed cash & bank.</span>
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <SummaryCard title="Opening Balance" cash={opening.cash} bank={opening.bank} />
              <SummaryCard title="Total Received" cash={received.cash} bank={received.bank} />
              <SummaryCard title="Total Paid" cash={paid.cash} bank={paid.bank}
                cashColor="text-red-600" bankColor="text-red-600" />
              <SummaryCard title="Current Balance" cash={closing.cash} bank={closing.bank} />
            </div>

            <div className="sticky top-0 z-30 bg-gray-50 pt-1 pb-2 -mx-1 px-1">
              <h3 className="text-base font-bold text-gray-700 mb-2">Transaction Details</h3>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => setActiveTab('received')}
                  className={`flex items-center justify-between gap-2 rounded-xl border px-3 sm:px-4 py-2.5 text-left transition-colors ${
                    activeTab === 'received'
                      ? 'border-green-500 bg-green-50 ring-1 ring-green-200'
                      : 'border-gray-200 bg-white hover:border-green-300'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <ArrowDownLeft className={`w-4 h-4 shrink-0 ${activeTab === 'received' ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="min-w-0">
                      <span className={`block text-xs sm:text-sm font-semibold ${activeTab === 'received' ? 'text-green-700' : 'text-gray-600'}`}>
                        Received <span className="text-gray-400 font-normal">({receivedCount})</span>
                      </span>
                      <span className="block text-sm sm:text-base font-bold text-green-700 truncate">{money(receivedTotal)}</span>
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('paid')}
                  className={`flex items-center justify-between gap-2 rounded-xl border px-3 sm:px-4 py-2.5 text-left transition-colors ${
                    activeTab === 'paid'
                      ? 'border-red-500 bg-red-50 ring-1 ring-red-200'
                      : 'border-gray-200 bg-white hover:border-red-300'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <ArrowUpRight className={`w-4 h-4 shrink-0 ${activeTab === 'paid' ? 'text-red-600' : 'text-gray-400'}`} />
                    <span className="min-w-0">
                      <span className={`block text-xs sm:text-sm font-semibold ${activeTab === 'paid' ? 'text-red-700' : 'text-gray-600'}`}>
                        Paid <span className="text-gray-400 font-normal">({paidCount})</span>
                      </span>
                      <span className="block text-sm sm:text-base font-bold text-red-700 truncate">{money(paidTotal)}</span>
                    </span>
                  </span>
                </button>
              </div>
            </div>

            <div className="mt-4" />

            {activeTab === 'received' ? (
              receivedCount === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-500">
                  No received transactions for the selected period.
                </div>
              ) : (
                <>
                  <TxnTable title="Cash Transactions" txns={receivedCash} accent="text-green-600"
                    onEdit={openEditEntry} onDelete={handleDeleteEntry} />
                  <TxnTable title="Bank / Online Transactions" txns={receivedBank} accent="text-green-600"
                    onEdit={openEditEntry} onDelete={handleDeleteEntry} />
                </>
              )
            ) : (
              paidCount === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-500">
                  No paid transactions for the selected period.
                </div>
              ) : (
                <>
                  <TxnTable title="Cash Transactions" txns={paidCash} accent="text-red-600"
                    onEdit={openEditEntry} onDelete={handleDeleteEntry} />
                  <TxnTable title="Bank / Online Transactions" txns={paidBank} accent="text-red-600"
                    onEdit={openEditEntry} onDelete={handleDeleteEntry} />
                </>
              )
            )}
          </>
        )}
      </div>

      <CashbookOpeningBalanceModal
        open={openingOpen}
        onClose={() => setOpeningOpen(false)}
        onSaved={fetchReport}
      />
      <CashbookEntryModal
        open={entryOpen}
        entry={editingEntry}
        onClose={() => { setEntryOpen(false); setEditingEntry(null); }}
        onSaved={fetchReport}
      />
    </DashboardLayout>
  );
};

export default CashbookReport;
